import React, {createRef, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import {Button} from "primereact/button";
import DocumentSettings from "../document/DocumentSettings";
import ContentMode from "../ContentMode";
import {ViewerMode} from "../viewer/ViewerMode";
import {TextSearchGlobals} from "../document/components/pdf/TextSearch";
import PdfViewer, {PdfViewerDefinition} from "../viewer/pdf/PdfViewer";
import DefaultViewer from "../viewer/DefaultViewer";
import PageViewMode from "../viewer/settings/PageViewMode";
import ImageViewer, {ImageViewerDefinition} from "../viewer/image/ImageViewer";
import ViewerType from "../document/DataType";
import HeicViewer, {HeicViewerDefinition} from "../viewer/image/HeicViewer";
import TiffViewer, {TiffViewerDefinition} from "../viewer/image/TiffViewer";
import HtmlViewer, {HtmlViewerDefinition} from "../viewer/html/HtmlViewer";
import RtfViewer, {RtfViewerDefinition} from "../viewer/rtf/RtfViewer";
import ZipViewer, {ZipViewerDefinition} from "../viewer/zip/ZipViewer";
import TextViewer, {TextViewerDefinition} from "../viewer/text/TextViewer";
import PortalContext from "../PortalContext";
import Immutable from "../../shared/immutable/Immutable";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import MimeType from "../document/mimetype/MimeType";
import MimeTypeFilter from "../document/mimetype/MimeTypeFilter";
import MimeTypes from "../document/mimetype/MimeTypes";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import LoadingViewer from "../viewer/LoadingViewer";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export const PREVIEW_SIZE: number = 150;

interface PreviewProperties extends TextSearchGlobals, I18nGlobals, DebugGlobals {
	context: PortalContext;
	portalDocument: Immutable<PortalDocumentInterface>;
	contentMode: ContentMode;
	controls?: boolean;
	size?: number;
	navigateToDestination?: (dest: string | Array<any> | null) => Promise<void>;
}

interface PreviewState {
	numberOfPages: number;
}

export class PreviewDefinition extends PortalPureComponent<PreviewProperties, PreviewState> {
	private viewerElement: RefObject<HTMLDivElement>;

	/**
	 * Constructs a {@link Preview} component
	 */
	constructor(props: PreviewProperties) {
		super(props);

		this.state = {
			numberOfPages: 0
		};

		this.viewerElement = createRef<HTMLDivElement>();

		this.viewPage = this.viewPage.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.setNumberOfPages();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PreviewProperties>, prevState: Readonly<PreviewState>
	): Promise<void> {
		await this.setNumberOfPages();
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let numberOfPages: number = await portalDocument.getNumberOfPages();

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	/**
	 * Show the given document and selected page in the main viewer
	 * @private
	 */
	private viewPage(): void {
		if (!FunctionHelper.exists(this.props.portalDocument)) {
			return;
		}

		this.props.context.documentManager.open(this.props.portalDocument.get().id!);
	}

	private renderPortfolioViewer(portalDocument: PortalDocument): ReactNode {
		if (portalDocument.isLocked()) {
			return <LoadingViewer {...this.props} viewerMode={ViewerMode.PORTFOLIO_PREVIEW}/>;
		}

		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let isFolderSelected: boolean = FunctionHelper.exists(portalDocument.getFolderByPath(
			FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
		));

		let selectedAttachment: Attachment | undefined = portalDocument.getAttachment(
			!isFolderSelected ? FunctionHelper.applyValue(documentSettings.portfolioSelection, "") : ""
		);

		if (!FunctionHelper.exists(selectedAttachment)) {
			return null;
		}

		let mimeType: MimeType = portalDocument.getMimeType(selectedAttachment);
		let viewerType: ViewerType = portalDocument.getViewerType(selectedAttachment);

		if (MimeTypeFilter.checkSelection([mimeType], [MimeTypes.DOC, MimeTypes.DOCX])) {
			viewerType = ViewerType.DEFAULT;
		}

		switch (viewerType) {
			case ViewerType.PDF:
				return <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props}
								  attachmentKey={selectedAttachment!.objectKey}
								  viewerMode={ViewerMode.PORTFOLIO_PREVIEW}
								  currentPage={viewerSettings.activeViewer.currentPage}/>;
			case ViewerType.IMAGE:
				return <ImageViewer {...ImageViewerDefinition.defaults()} {...this.props}
									attachmentKey={selectedAttachment!.objectKey}
									viewerMode={ViewerMode.PORTFOLIO_PREVIEW}/>;
			case ViewerType.HEIC:
				return <HeicViewer {...HeicViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PREVIEW}
								   currentPage={viewerSettings.activeViewer.currentPage}/>;
			case ViewerType.TIFF:
				return <TiffViewer {...TiffViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PREVIEW}
								   currentPage={viewerSettings.activeViewer.currentPage}/>;
			case ViewerType.TEXT:
				return <TextViewer {...TextViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PREVIEW}/>;
			case ViewerType.ZIP:
				return <ZipViewer {...ZipViewerDefinition.defaults()} {...this.props} {...this.state}
								  attachmentKey={selectedAttachment!.objectKey}
								  viewerMode={ViewerMode.PORTFOLIO_PREVIEW}/>;
			case ViewerType.HTML:
				return <HtmlViewer {...HtmlViewerDefinition.defaults()} {...this.props} {...this.state}
								   attachmentKey={selectedAttachment!.objectKey} viewAsHTML={viewerSettings.viewAsHTML}
								   viewerMode={ViewerMode.PORTFOLIO_PREVIEW}/>;
			case ViewerType.RTF:
				return <RtfViewer {...RtfViewerDefinition.defaults()} {...this.props} {...this.state}
								  attachmentKey={selectedAttachment!.objectKey}
								  viewerMode={ViewerMode.PORTFOLIO_PREVIEW}/>;
		}

		return <DefaultViewer {...this.props} {...this.state} attachmentKey={selectedAttachment!.objectKey}
							  portalDocument={this.props.portalDocument!} viewerMode={ViewerMode.PORTFOLIO_PREVIEW}
							  mouseMode={documentSettings.activeViewer.mouseMode}/>;
	}

	private renderViewer(portalDocument: PortalDocument): ReactNode {
		if (portalDocument.isLocked()) {
			return <LoadingViewer {...this.props} viewerMode={ViewerMode.PREVIEW}/>;
		}

		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (portalDocument.isPortfolio() && !FunctionHelper.isEmpty(documentSettings.portfolioSelection)) {
			return this.renderPortfolioViewer(portalDocument);
		}

		switch (portalDocument.getViewerType()) {
			case ViewerType.PDF:
				return <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PREVIEW}
								  currentPage={documentSettings.activeViewer.currentPage}/>;
			case ViewerType.IMAGE:
				return <ImageViewer {...ImageViewerDefinition.defaults()} {...this.props}
									viewerMode={ViewerMode.PREVIEW}/>;
			case ViewerType.HEIC:
				return <HeicViewer {...HeicViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PREVIEW}
								   currentPage={documentSettings.activeViewer.currentPage}/>;
			case ViewerType.TIFF:
				return <TiffViewer {...TiffViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PREVIEW}
								   currentPage={documentSettings.activeViewer.currentPage}/>;
			case ViewerType.HTML:
				return <HtmlViewer {...HtmlViewerDefinition.defaults()} {...this.props}
								   viewAsHTML={documentSettings.viewAsHTML} viewerMode={ViewerMode.PREVIEW}/>;
			case ViewerType.RTF:
				return <RtfViewer {...RtfViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PREVIEW}/>;
			case ViewerType.ZIP:
				return <ZipViewer {...ZipViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PREVIEW}/>;
			case ViewerType.TEXT:
				return <TextViewer {...TextViewerDefinition.defaults()} {...this.props}
								   viewerMode={ViewerMode.PREVIEW}/>;
		}

		return <DefaultViewer {...this.props} {...this.state} portalDocument={this.props.portalDocument!}
							  viewerMode={ViewerMode.PREVIEW} mouseMode={documentSettings.activeViewer.mouseMode}/>;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		return (
			<>
				{this.renderViewer(portalDocument)}
				{this.props.controls && documentSettings.activeViewer.currentPage > 1 &&
					<Button className="button-previous" icon="icon icon-previous"
							onClick={() => {
								documentSettings.activeViewer.previousPage(PageViewMode.SINGLE);
								this.props.context.documentManager.updateGlobalDocumentList();
							}}/>
				}
				{this.props.controls && documentSettings.activeViewer.currentPage < this.state.numberOfPages &&
					<Button className="button-next" icon="icon icon-next"
							onClick={() => {
								documentSettings.activeViewer.nextPage(this.state.numberOfPages, PageViewMode.SINGLE);
								this.props.context.documentManager.updateGlobalDocumentList();
							}}/>
				}
				{this.props.controls &&
					<Button className="button-view" icon="icon icon-eye" onClick={this.viewPage}/>
				}
			</>
		);
	}
}

const Preview = React.memo(PreviewDefinition,
	(prevProps: Readonly<PreviewProperties>, nextProps: Readonly<PreviewProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			if (nextProps.portalDocument.get().id !== prevProps.portalDocument.get().id) {
				return false;
			}

			if (nextProps.portalDocument.get().updated !== prevProps.portalDocument.get().updated) {
				return false;
			}

			if (nextProps.portalDocument.get().settings.viewerSettings.currentPage !==
				prevProps.portalDocument.get().settings.viewerSettings.currentPage) {
				return false;
			}
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		return true;
	}
);

export default Preview;